import defaultSwitchOffTimes from "./defaultSwitchOffTimes";

const NEW_LOCATION_ID = -1;

export default class ClientData {
    constructor(data = {}) {
        this.update(data);
    }

    update(data = {}) {
        const providedLocations = data.locations || [];
        const locations = providedLocations.map(location => {
            const saneLatitude = location.latitude?.replaceAll('_', '0');
            const saneLongitude = location.longitude?.replaceAll('_', '0');
            return {...location,
                value: location.id,
                text: location.displayName,
                latitude: saneLatitude,
                longitude: saneLongitude
            };
        });

        const saneLatitude = data.latitude?.replaceAll('_', '0');
        const saneLongitude = data.longitude?.replaceAll('_', '0');

        this.active = data.active || false;
        this.alias = data.alias || '';
        this.defaultLocation = data.defaultLocation || '';
        this.dynamicFields = data.dynamicFields || '';
        this.latitude = saneLatitude;
        this.longitude = saneLongitude;
        this.locationId = data.locationId || '';
        this.locationName = data.locationName || '';
        this.locationPlan = data.locationPlan || '';
        this.locationPlanX = data.locationPlanX || '';
        this.locationPlanY = data.locationPlanY || '';
        this.locations = locations;
        this.switchOffTimes = this.getSwitchOffTimes(data);
        this.switchOffTimesActive = data.switchOffTimesActive || '';
        this.teamviewer = data.teamviewer || '';
        this.locationPlanNew = undefined;
        this.hasNewLocationPlan = false;
    }

    getSwitchOffTimes(data) {
        if (!data) return undefined;
        if (!data.switchOffTimes) return this.getDefaultSwitchOffTimes();
        if (_.isArray(data.switchOffTimes)) {
            if (data.switchOffTimes.length === 0) return this.getDefaultSwitchOffTimes();
        }

        return data.switchOffTimes;
    }

    getDefaultSwitchOffTimes() {
        return {
            all: {
                active: false,
                project: true,
                from: defaultSwitchOffTimes.startTime,
                to: defaultSwitchOffTimes.endTime
            },
            days: defaultSwitchOffTimes.days
        }
    }

    isLatInvalid() {
        const {latitude} = this.getGeoCoords();
        if (!latitude) return false;

        const parsed = parseFloat(latitude);
        return !_.inRange(parsed, -85,85);
    }

    isLongInvalid() {
        const {longitude} = this.getGeoCoords();
        if (!longitude) return false;

        const parsed = parseFloat(longitude);
        return !_.inRange(parsed, -180,180);
    }

    isInvalid() {
        const invalidName = !this.getLocationName();
        const invalidLat = this.isLatInvalid();
        const invalidLong = this.isLongInvalid();
        return invalidName || invalidLat  || invalidLong;
    }

    addNewLocationPlan(newPlan) {
        if (this.addNewLocationModeActive()) {
            this.locationPlanNew = newPlan;
        }
        this.getCurrentLocation().planFile = newPlan;
        this.getCurrentLocation().hasNewFile = true;
    }

    deleteLocationPlan() {
        if (this.addNewLocationModeActive()) {
            this.locationPlanNew = null;
        }
        this.getCurrentLocation().planFile = null;
    }

    getLocations({canAddNew = false} = {}) {
        if (!canAddNew) return this.locations;

        const newLocationLabel = Lang.langGet('New Location');
        const newLocation = {id: NEW_LOCATION_ID, value: NEW_LOCATION_ID, name: newLocationLabel, text: newLocationLabel};
        return [...this.locations, newLocation];
    }

    getCurrentLocation() {
        const locations = this.getLocations({canAddNew: true});
        return locations.find(location => location.id === this.locationId);
    }

    getLocationName() {
        if (this.addNewLocationModeActive()) {
            return this.locationName;
        }
        return this.getCurrentLocation()?.displayName;
    }

    addNewLocationModeActive() {
        return this.locationId === NEW_LOCATION_ID;
    }

    getLocationPlan() {
        if (this.addNewLocationModeActive()) {
            return this.locationPlanNew;
        }
        return this.getCurrentLocation()?.planFile;
    }

    getLocationPlanStoragePath(projectId) {
        if (this.addNewLocationModeActive()) {
            return this.locationPlanNew;
        }

        const currentLocation = this.getCurrentLocation();
        if (currentLocation?.hasNewFile) return currentLocation.planFile;
        else return `/storage/fields/${projectId}/${currentLocation?.planFile}`;
    }

    getGeoCoords() {
        if (this.addNewLocationModeActive()) {
            return {latitude: this.latitude, longitude: this.longitude};
        }

        const location = this.getCurrentLocation();
        if (!location) return {};

        const {latitude, longitude} = location;
        return {latitude, longitude};
    }

    updateProperty(property, value) {
        switch (property) {
            case 'switchOffTimes': {
                this.switchOffTimes = value;
                break;
            }
            case 'locationId': {
                this[property] = value;
                if (value === -1) {
                    this.locationName = '';
                }
                else {
                    this.locationName = this.getCurrentLocation().name;
                    this.latitude = this.getCurrentLocation().latitude;
                    this.longitude =this.getCurrentLocation().longitude;
                }
                break;
            }
            case 'latitude':{
                if (!this.addNewLocationModeActive()) {
                    this.getCurrentLocation().latitude = value;
                }
                this.latitude = value;
                break;
            }
            case 'longitude': {
                if (!this.addNewLocationModeActive()) {
                    this.getCurrentLocation().longitude = value;
                }
                this.longitude = value;
                break;
            }
            case 'name': {
                if (!this.addNewLocationModeActive()) {
                    this.locationName = value;
                    this.getCurrentLocation().text = value;
                    this.getCurrentLocation().displayName = value;
                } else {
                    this.locationName = value;
                }
                break;
            }
            default: {
                this[property] = value;
            }
        }
    }

    updateCoordinates(x, y) {
        this.locationPlanX = x;
        this.locationPlanY = y;
    }

    isDefaultLocation() {
        return this.locationId === this.defaultLocation;
    }

    /**
     * @param data {ClientData}
     * @param geoProperty {('longitude' | 'latitude')}
     * @return {string}
     */
    prepareGeoChord(data, geoProperty) {
        if (!data[geoProperty]) return '0.000000';
        const [base, comma] = data[geoProperty].split('.');
        if (comma) return data[geoProperty];

        return `${base}.000000`;
    }

    /**
     * @param data {ClientData}
     * @returns {{latitude: string, longitude: string}}
     */
    prepareGeoChoords(data) {
        const longitude = this.prepareGeoChord(data, 'longitude');
        const latitude = this.prepareGeoChord(data, 'latitude');

        return {longitude, latitude};
    }

    prepareForAPIRequest() {
        const data = _.cloneDeep(this);

        delete data.locations;
        delete data.locationPlanNew;
        data.locationPlan = this.getLocationPlan();

        const {latitude, longitude} = this.prepareGeoChoords(data);
        data.latitude = latitude;
        data.longitude = longitude;
        return data;
    }
}
